import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import CustomisableRibbon from 'src/components/customisable-ribbon/CustomisableRibbon';
import DeleteModal from 'src/components/delete-modal';
import ExportButton from 'src/components/export-button';
import { useRibbonAndExport } from 'src/hooks/useRibbonAndExport';
import { PageLayout } from 'src/layouts';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useDeleteOversightResults } from '@/hooks/mutations/oversight-result/useDeleteOversightResults';
import { useGetAllOversightAssessmentResults } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';

import { useGetCollectionTableProps } from './config';
import { OversightResultRegisterToolbar } from './OversightResultRegisterToolbar';
import type { OversightAssessmentResultRegisterFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const OversightAssessmentResultsPage: FC = () => {
  const { t: st } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });
  const { t } = useTranslation('common');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const { data, loading } = useGetAllOversightAssessmentResults({
    queryArgs: {},
  });
  const [selectedOversightResults, setSelectedOversightResults] = useState<
    OversightAssessmentResultRegisterFields[]
  >([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  const {
    hasPermission: canDeleteDocumentOversightResult,
    loading: canDeleteDocumentOversightResultLoading,
  } = useHasPermissionQuery('delete:document_oversight_result');
  const {
    hasPermission: canDeleteObligationOversightResult,
    loading: canDeleteObligationOversightResultLoading,
  } = useHasPermissionQuery('delete:obligation_oversight_result');
  const {
    hasPermission: canDeleteRiskControlledOversightResult,
    loading: canDeleteRiskControlledOversightResultLoading,
  } = useHasPermissionQuery('delete:risk_controlled_oversight_result');
  const {
    hasPermission: canDeleteRiskUncontrolledOversightResult,
    loading: canDeleteRiskUncontrolledOversightResultLoading,
  } = useHasPermissionQuery('delete:risk_uncontrolled_oversight_result');
  const {
    hasPermission: canDeleteControlTestOversightResult,
    loading: canDeleteControlTestOversightResultLoading,
  } = useHasPermissionQuery('delete:control_test_oversight_result');
  const isLoading =
    canDeleteDocumentOversightResultLoading ||
    canDeleteObligationOversightResultLoading ||
    canDeleteRiskControlledOversightResultLoading ||
    canDeleteRiskUncontrolledOversightResultLoading ||
    canDeleteControlTestOversightResultLoading;
  const canDeleteOversightResult =
    !isLoading &&
    (canDeleteDocumentOversightResult ||
      canDeleteObligationOversightResult ||
      canDeleteRiskControlledOversightResult ||
      canDeleteRiskUncontrolledOversightResult ||
      canDeleteControlTestOversightResult);

  const { deleteOversightResults, loading: deleteLoading } =
    useDeleteOversightResults();

  const onDelete = useDeleteResultNotification({
    entityName: st('entity_name'),
    asyncAction: async () => {
      await deleteOversightResults(
        selectedOversightResults?.map((s) => s.originalResult.Id) ?? []
      );
      setSelectedOversightResults([]);
      setIsDeleteModalVisible(false);

      return true;
    },
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
  });

  const navigate = useNavigate();

  const handleOpenRating = (id: string) => {
    navigate(id);
  };

  const tableProps = useGetCollectionTableProps(
    [
      ...(data?.document_oversight_result || []),
      ...(data?.obligation_oversight_result || []),
      ...(data?.control_test_oversight_result || []),
      ...(data?.risk_controlled_oversight_result?.map((c) => ({
        ...c,
        ControlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
      })) || []),
      ...(data?.risk_uncontrolled_oversight_result?.map((c) => ({
        ...c,
        ControlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
      })) || []),
    ],
    handleOpenRating
  );

  const assessmentResultCount = useMemo(() => {
    if (loading) {
      return '';
    }

    return `(${tableProps.allItems?.length})`;
  }, [loading, tableProps]);

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  const title = t('navigationMenu.assessments.oversightFindingsTitle');

  return (
    <PageLayout
      helpTranslationKey={'assessmentResults.registerHelp'}
      title={title}
      counter={assessmentResultCount}
      // Object-level toolbar (CUS-164) — flag-gated. Export moves into the
      // toolbar's Download group; the selection-driven bulk-delete moves into the
      // Danger zone (gated on the same compound permission as the legacy button).
      headerOverride={
        objectToolbarEnabled ? (
          <OversightResultRegisterToolbar
            title={title}
            counter={assessmentResultCount}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
            hasDeletePermission={canDeleteOversightResult}
            isDeleteDisabled={!selectedOversightResults.length}
            onDelete={() => setIsDeleteModalVisible(true)}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xxs'}>
          {canDeleteOversightResult && (
            <Button
              formAction={'none'}
              variant={'normal'}
              disabled={!selectedOversightResults.length}
              onClick={() => setIsDeleteModalVisible(true)}
            >
              {st('delete_button')}
            </Button>
          )}
          <ExportButton
            tableProps={tableProps}
            entityLabel={title}
            {...ribbonExportProps}
          />
        </SpaceBetween>
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.ComplianceMonitoringAssessmentResult}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />
      <Table
        {...tableProps}
        loading={loading}
        selectionType={canDeleteOversightResult ? 'multi' : undefined}
        selectedItems={selectedOversightResults}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => {
          setSelectedOversightResults(detail.selectedItems);
        }}
      />
      <DeleteModal
        isVisible={isDeleteModalVisible}
        loading={deleteLoading}
        header={st('delete_button')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default OversightAssessmentResultsPage;
